import React, { useEffect, memo } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  useCreateMutation,
  useUpdatePatchMutation,
  useDetailQuery,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { handleFormErrors } from 'utils/formErrorHandler';
import FormField from 'components/Form/Inputs/FormField';

interface CatalogItemFormValues {
  name: string;
  categoryId: string;
  sku: string;
  description: string;
  imageUrl: string;
  isActive: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string()
    .trim()
    .required('Name is required')
    .max(250, 'Name must be at most 250 characters'),
  categoryId: Yup.string().trim().required('Category is required'),
  sku: Yup.string().trim().max(50, 'SKU must be at most 50 characters'),
  description: Yup.string().trim().max(1000, 'Description must be at most 1000 characters'),
  imageUrl: Yup.string()
    .trim()
    .url('Must be a valid URL')
    .max(500, 'Image URL must be at most 500 characters'),
  isActive: Yup.boolean(),
});

interface CatalogItemCreateOrUpdateProps {
  isOpen: boolean;
  toggle: () => void;
  itemId?: string;
  onSuccess?: () => void;
}

const CatalogItemCreateOrUpdate: React.FC<CatalogItemCreateOrUpdateProps> = ({
  isOpen,
  toggle,
  itemId,
  onSuccess,
}) => {
  const isEditMode = Boolean(itemId);

  const { data: fetchedItem, isLoading: loadingItem } = useDetailQuery<CatalogItemFormValues>(
    API_ENDPOINTS.SAAS.CATALOG_ITEMS.GET_BY_ID(itemId ?? ''),
    {
      id: itemId ?? '',
    },
  );

  const { mutate: createItem, isPending: creating } = useCreateMutation(
    API_ENDPOINTS.SAAS.CATALOG_ITEMS.BASE,
  );

  const { mutate: updateItem, isPending: updating } = useUpdatePatchMutation(
    `${API_ENDPOINTS.SAAS.CATALOG_ITEMS.BASE}/${itemId}`,
  );

  const initialValues: CatalogItemFormValues = {
    name: '',
    categoryId: '',
    sku: '',
    description: '',
    imageUrl: '',
    isActive: true,
    ...fetchedItem,
  };

  const formik = useFormik<CatalogItemFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<CatalogItemFormValues>) => {
      const payload = {
        ...values,
        name: values.name.trim(),
        categoryId: values.categoryId,
        sku: values.sku?.trim() || undefined,
        description: values.description?.trim() || undefined,
        imageUrl: values.imageUrl?.trim() || undefined,
      };

      const handleSuccess = () => {
        formik.resetForm();
        toggle();
        onSuccess?.();
      };

      const handleError = (error: any) => {
        handleFormErrors<CatalogItemFormValues>(error, formikHelpers);
      };

      if (isEditMode) {
        updateItem(
          { data: values, params: { id: itemId } },
          { onSuccess: handleSuccess, onError: handleError },
        );
      } else {
        createItem(payload, { onSuccess: handleSuccess, onError: handleError });
      }
    },
  });

  const handleClose = () => {
    formik.resetForm();
    toggle();
  };

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg">
      <ModalHeader toggle={handleClose}>
        {isEditMode ? 'Update Catalog Item' : 'Create Catalog Item'}
      </ModalHeader>
      <form onSubmit={formik.handleSubmit} noValidate>
        <ModalBody>
          {isEditMode && loadingItem ? (
            <div className="text-center py-4">
              <Spinner color="primary" />
            </div>
          ) : (
            <>
              <FormGroup>
                <Label for="name">Name *</Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Enter item name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.name && Boolean(formik.errors.name)}
                />
                {formik.touched.name && formik.errors.name && (
                  <small className="text-danger">{formik.errors.name}</small>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="categoryId">Category *</Label>
                <Input
                  id="categoryId"
                  name="categoryId"
                  type="text"
                  placeholder="Enter category ID"
                  value={formik.values.categoryId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.categoryId && Boolean(formik.errors.categoryId)}
                />
                {formik.touched.categoryId && formik.errors.categoryId && (
                  <small className="text-danger">{formik.errors.categoryId}</small>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="sku">SKU</Label>
                <Input
                  id="sku"
                  name="sku"
                  type="text"
                  placeholder="Enter SKU"
                  value={formik.values.sku}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.sku && Boolean(formik.errors.sku)}
                />
                {formik.touched.sku && formik.errors.sku && (
                  <small className="text-danger">{formik.errors.sku}</small>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="description">Description</Label>
                <Input
                  id="description"
                  name="description"
                  type="textarea"
                  placeholder="Enter description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.description && Boolean(formik.errors.description)}
                />
                {formik.touched.description && formik.errors.description && (
                  <small className="text-danger">{formik.errors.description}</small>
                )}
              </FormGroup>

              <FormGroup>
                <Label for="imageUrl">Image URL</Label>
                <Input
                  id="imageUrl"
                  name="imageUrl"
                  type="text"
                  placeholder="Enter image URL"
                  value={formik.values.imageUrl}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.imageUrl && Boolean(formik.errors.imageUrl)}
                />
                {formik.touched.imageUrl && formik.errors.imageUrl && (
                  <small className="text-danger">{formik.errors.imageUrl}</small>
                )}
              </FormGroup>

              <FormGroup check>
                <Label check>
                  <Input
                    name="isActive"
                    type="checkbox"
                    checked={formik.values.isActive}
                    onChange={formik.handleChange}
                  />
                  Active
                </Label>
              </FormGroup>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={creating || updating || loadingItem}>
            {creating || updating ? (
              <>
                <Spinner size="sm" color="light" /> {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : isEditMode ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default memo(CatalogItemCreateOrUpdate);
